import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';

import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import { PaymentMethodPaymentsSection } from 'pages/Payments/components/PaymentMethodPaymentsSection';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import {
    MealPlanDefinitionFragmentFragment,
    useGetMealPlanDefinitionQuery,
    useUpdateMealPlanDefinitionMutation,
    TotadminPermission,
} from 'data/__generated__';

import { EligibleCategoriesSection } from './components/EligibleCategoriesSection';
import { GeneralInfoSection } from './components/GeneralInfoSection';
import { ImagesSection } from './components/ImagesSection';
import { toFormData, toUpdateInput, type MealPlanDefinitionFormData } from './constants/formData';

const ACTION_GROUPS: PageActionGroup[] = [
    {
        label: 'Navigation',
        items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.mealPlanDefinitions.url }],
    },
];

/**
 * Le formulaire est monté APRÈS la lecture, avec les valeurs de la formule en `defaultValues`.
 * C'est ce que `useFieldArray` exige (une liste absente des valeurs initiales reste vide), et c'est
 * aussi ce qui garantit un `isDirty` juste à l'ouverture, sans ré-étalonnage.
 */
const MealPlanDefinitionForm = ({ mealPlanDefinition }: { mealPlanDefinition: MealPlanDefinitionFragmentFragment }) => {
    const methods = useForm<MealPlanDefinitionFormData>({ defaultValues: toFormData(mealPlanDefinition) });
    const {
        formState: { isDirty },
        reset,
    } = methods;

    const [updateMealPlanDefinition, { loading }] = useUpdateMealPlanDefinitionMutation();

    const onSubmit = methods.handleSubmit(
        async (values) => {
            try {
                const { data } = await updateMealPlanDefinition({
                    variables: { mealPlanDefinitionId: mealPlanDefinition._id, input: toUpdateInput(values) },
                });

                if (!data?.updateMealPlanDefinition) {
                    throw new Error("Une erreur inconnue s'est produite");
                }

                toast.success('La formule a bien été mise à jour !');
                // On repart des valeurs RENDUES par l'API : les fichiers déposés y sont devenus des URLs.
                reset(toFormData(data.updateMealPlanDefinition));
            } catch (error) {
                toast.error(error instanceof Error ? error.message : 'La formule n’a pas pu être mise à jour', {
                    autoClose: false,
                });
            }
        },
        () => toast.error("Le formulaire n'est pas valide, vérifiez les champs en rouge"),
    );

    return (
        <FormProvider {...methods}>
            <Form onSubmit={onSubmit}>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.mealPlanDefinitionDetails} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={ACTION_GROUPS}
                        onSave={onSubmit}
                        saveDisabled={!isDirty}
                        saveLoading={loading}
                        saveTitle="Mettre à jour"
                        saveRequiredPermissions={[TotadminPermission.MealPlanDefinitionsManage]}
                    />
                </Header>
                <Content>
                    <ScrollableContent>
                        <GeneralInfoSection mealPlanDefinition={mealPlanDefinition} />
                        <EligibleCategoriesSection />
                        <ImagesSection />
                        <PaymentMethodPaymentsSection
                            paymentMethodId={mealPlanDefinition._id}
                            emptyMessage="Aucun paiement avec cette formule"
                            dataTest="meal-plan-definition-payments"
                        />
                        <AuditLogsSection collectionDocumentIds={[mealPlanDefinition._id]} />
                    </ScrollableContent>
                </Content>
            </Form>
        </FormProvider>
    );
};

export const MealPlanDefinitionDetails = () => {
    const { mealPlanDefinitionId = '' } = useParams<{ mealPlanDefinitionId: string }>();

    const { loading, data, error } = useGetMealPlanDefinitionQuery({ variables: { mealPlanDefinitionId } });

    if (loading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error || !data) {
        throw new Error('Une erreur est survenue lors de la récupération de la formule');
    }

    return (
        <Container>
            <MealPlanDefinitionForm mealPlanDefinition={data.mealPlanDefinition} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
